/*
  ALU Store — ficha de producto (sections/alu-main-product.liquid y
  alu-related-products.liquid): galería con zoom, variantes, cantidad, barra fija
  de compra, calculador de envío y carga de recomendaciones.
*/
(() => {
  const define = (name, klass) => {
    if (!customElements.get(name)) customElements.define(name, klass);
  };

  // Mismo criterio que snippets/alu-price.liquid: "S/" sin punto y siempre dos decimales.
  const formatMoney = (cents, format) => formatShopMoney(cents, format).replace('S/.', 'S/');
  const formatShopMoney = (cents, format) => {
    const amount = (Number(cents) / 100).toFixed(2);
    const [integer, decimals] = amount.split('.');
    const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      if (key === 'amount_no_decimals') return withThousands;
      if (key === 'amount_with_comma_separator') return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
      return `${withThousands}.${decimals}`;
    });
  };

  define('alu-product', class extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      this.product = json ? JSON.parse(json.textContent) : null;
      this.moneyFormat = this.dataset.moneyFormat;
      const inventory = this.querySelector('[data-inventory-json]');
      try {
        this.inventory = inventory ? JSON.parse(inventory.textContent) : {};
      } catch (error) {
        this.inventory = {};
      }

      this.stage = this.querySelector('[data-gallery-stage]');
      this.mainImage = this.stage?.querySelector(':scope > img');
      this.thumbs = [...this.querySelectorAll('.alu-thumb')];
      this.activeThumb = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      this.counter = this.querySelector('[data-gallery-counter]');

      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.showImage(index)));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showImage(this.activeThumb - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showImage(this.activeThumb + 1));

      this.querySelectorAll('input[data-option-index]').forEach((input) =>
        input.addEventListener('change', () => this.onOptionChange())
      );

      this.bindZoom();
      this.bindQuantity();
      this.bindStickyBar();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* ---------- Galería ---------- */
    showImage(index) {
      if (!this.thumbs.length || !this.mainImage) return;
      this.activeThumb = (index + this.thumbs.length) % this.thumbs.length;
      const thumb = this.thumbs[this.activeThumb];
      this.mainImage.src = thumb.dataset.src;
      this.mainImage.srcset = thumb.dataset.srcset;
      this.mainImage.alt = thumb.dataset.alt;
      this.thumbs.forEach((item, i) => {
        const isActive = i === this.activeThumb;
        item.classList.toggle('is-active', isActive);
        if (isActive) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = `${this.activeThumb + 1} / ${this.thumbs.length}`;
    }

    bindZoom() {
      const dialog = this.querySelector('[data-zoom-dialog]');
      const image = dialog?.querySelector('[data-zoom-image]');
      if (!dialog || !image || !this.mainImage) return;
      this.querySelector('[data-zoom-open]')?.addEventListener('click', () => {
        image.src = this.mainImage.currentSrc || this.mainImage.src;
        image.alt = this.mainImage.alt;
        dialog.showModal();
      });
      dialog.querySelector('[data-zoom-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close(); // clic en el fondo
      });
    }

    showMedia(mediaId) {
      const index = this.thumbs.findIndex((thumb) => Number(thumb.dataset.mediaId) === Number(mediaId));
      if (index > -1) this.showImage(index);
    }

    /* ---------- Variantes ---------- */
    onOptionChange() {
      if (!this.product) return;
      const selected = [...this.querySelectorAll('fieldset.alu-variant-field')].map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
      selected.forEach((value, index) => {
        const label = this.querySelector(`[data-option-label="${index}"]`);
        if (label && value) label.textContent = value;
      });

      const variant = this.product.variants.find((item) => item.options.every((option, i) => option === selected[i]));
      this.updateVariant(variant);
    }

    updateVariant(variant) {
      const idInput = this.querySelector('[data-variant-id]');
      const buttons = [
        ...this.querySelectorAll('[data-add-button], [data-buy-now], [data-sticky-add]'),
      ];
      // El texto va en [data-button-label] para no borrar el ícono del botón.
      const setLabel = (button, text) => {
        if (button.hasAttribute('data-buy-now')) return;
        (button.querySelector('[data-button-label]') || button).textContent = text;
      };
      const availability = this.querySelector('[data-availability]');

      if (!variant) {
        buttons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('data-unavailable', '');
          setLabel(button, 'No disponible');
        });
        if (availability) {
          availability.textContent = 'No disponible';
          availability.classList.add('is-unavailable');
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      const price = formatMoney(variant.price, this.moneyFormat);
      this.querySelector('[data-price]').textContent = price;
      const sticky = this.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = price;

      const compare = this.querySelector('[data-compare-price]');
      const badges = this.querySelectorAll('[data-saving-badge]');
      const savings = this.querySelector('[data-savings]');
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      const discount = hasDiscount ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
      if (compare) {
        compare.hidden = !hasDiscount;
        if (hasDiscount) compare.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
      }
      badges.forEach((badge) => {
        badge.hidden = !hasDiscount;
        badge.querySelector('[data-saving-value]').textContent = discount;
      });
      if (savings) {
        savings.hidden = !hasDiscount;
        if (hasDiscount) {
          savings.querySelector('[data-savings-value]').textContent = formatMoney(variant.compare_at_price - variant.price, this.moneyFormat);
        }
      }

      const skuWrapper = this.querySelector('[data-sku-wrapper]');
      if (skuWrapper) {
        skuWrapper.hidden = !variant.sku;
        skuWrapper.querySelector('[data-sku]').textContent = variant.sku || '';
      }
      if (availability) {
        availability.textContent = variant.available ? 'Disponible' : 'Agotado';
        availability.classList.toggle('is-unavailable', !variant.available);
      }

      buttons.forEach((button) => {
        button.disabled = !variant.available;
        button.toggleAttribute('data-unavailable', !variant.available);
        setLabel(button, variant.available ? 'Agregar al carrito' : 'Agotado');
      });

      const lowStock = this.querySelector('[data-low-stock]');
      if (lowStock) {
        const threshold = Number(lowStock.dataset.threshold || 0);
        const qty = this.inventory[String(variant.id)];
        const show = variant.available && threshold > 0 && qty !== null && qty !== undefined && qty <= threshold;
        lowStock.hidden = !show;
        if (show) lowStock.querySelector('[data-low-stock-qty]').textContent = qty;
      }

      this.dispatchEvent(new CustomEvent('alu:variant-change', { bubbles: true, detail: { variant } }));

      if (variant.featured_media) this.showMedia(variant.featured_media.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* ---------- Cantidad ---------- */
    bindQuantity() {
      const input = this.querySelector('[data-quantity-input]');
      const minus = this.querySelector('[data-quantity-minus]');
      const plus = this.querySelector('[data-quantity-plus]');
      if (!input) return;
      const update = (value) => {
        input.value = String(Math.max(1, Number.parseInt(value, 10) || 1));
        if (minus) minus.disabled = Number(input.value) <= 1;
        this.dispatchEvent(new CustomEvent('alu:quantity-change', { bubbles: true, detail: { quantity: Number(input.value) } }));
      };
      minus?.addEventListener('click', () => update(Number(input.value) - 1));
      plus?.addEventListener('click', () => update(Number(input.value) + 1));
      input.addEventListener('change', () => update(input.value));
    }

    /* ---------- Barra fija ---------- */
    bindStickyBar() {
      const bar = this.querySelector('[data-sticky-bar]');
      const actions = this.querySelector('[data-purchase-actions]');
      if (!bar || !actions || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          // Solo aparece cuando el botón de compra quedó ARRIBA de la pantalla.
          bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        },
        { threshold: 0 }
      );
      this.observer.observe(actions);
    }
  });

  /* ---------- Calculador de envío (zonas de Shopify + ubigeo) ---------- */
  const LOCATION_KEY = 'alu-delivery-location';
  const readLocation = () => {
    try {
      return JSON.parse(window.localStorage.getItem(LOCATION_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };
  const saveLocation = (location) => {
    try {
      window.localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
    } catch (error) {
      /* modo privado: la ubicación vale solo para esta vista */
    }
  };
  const jsonCache = {};
  const loadJson = (url) => {
    jsonCache[url] = jsonCache[url] || fetch(url).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
    return jsonCache[url];
  };
  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  define('alu-delivery-estimator', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('alu-product');
      this.moneyFormat = this.product?.dataset.moneyFormat;
      this.price = Number(this.dataset.price || 0);
      this.dialog = this.querySelector('[data-delivery-dialog]');
      this.form = this.querySelector('[data-delivery-form]');
      this.result = this.querySelector('[data-delivery-result]');
      this.initialResult = this.result.innerHTML;
      this.title = this.result.querySelector('strong')?.textContent.trim() || 'Envío a domicilio';
      this.caret = this.result.querySelector('[data-delivery-open] svg')?.outerHTML || '';
      this.selects = {
        department: this.querySelector('[data-location-select="department"]'),
        province: this.querySelector('[data-location-select="province"]'),
        district: this.querySelector('[data-location-select="district"]'),
      };
      this.eta = this.parseEta(this.dataset.eta || '');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-delivery-open]')) this.open();
      });
      this.querySelector('[data-delivery-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close(); // clic en el fondo
      });
      this.selects.department.addEventListener('change', () => this.fillProvinces());
      this.selects.province.addEventListener('change', () => this.fillDistricts());
      this.selects.district.addEventListener('change', () => this.toggleSave());
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.save();
      });

      this.product?.addEventListener('alu:variant-change', (event) => {
        this.price = Number(event.detail.variant.price);
        this.render();
      });

      this.location = readLocation();
      if (this.location) this.render();
    }

    /* "Zona = texto" por línea (ajuste del Theme Editor). */
    parseEta(text) {
      const map = {};
      text.split(/\r?\n/).forEach((line) => {
        const index = line.indexOf('=');
        if (index < 1) return;
        map[line.slice(0, index).trim().toLowerCase()] = line.slice(index + 1).trim();
      });
      return map;
    }

    async open() {
      this.dialog.showModal();
      try {
        this.ubigeo = await loadJson(this.dataset.ubigeoUrl);
      } catch (error) {
        console.error('[alu-delivery] No se pudo cargar el ubigeo', error);
        this.selects.department.innerHTML = '<option value="">No se pudo cargar la lista</option>';
        return;
      }
      this.fillSelect(this.selects.department, this.ubigeo.map((d) => d.n), 'Selecciona un departamento');
      const saved = this.location;
      if (saved) {
        this.selects.department.value = saved.department;
        this.fillProvinces(saved.province);
        this.selects.province.value = saved.province;
        this.fillDistricts(saved.district);
      } else {
        this.fillProvinces();
      }
      this.selects.department.focus();
    }

    fillSelect(select, names, placeholder, selected) {
      select.innerHTML =
        `<option value="">${escapeHtml(placeholder)}</option>` +
        names.map((name) => `<option value="${escapeHtml(name)}"${name === selected ? ' selected' : ''}>${escapeHtml(name)}</option>`).join('');
      select.disabled = names.length === 0;
    }

    currentDepartment() {
      return this.ubigeo?.find((d) => d.n === this.selects.department.value);
    }

    currentProvince() {
      return this.currentDepartment()?.p.find((p) => p.n === this.selects.province.value);
    }

    fillProvinces(selected) {
      const department = this.currentDepartment();
      this.fillSelect(this.selects.province, department ? department.p.map((p) => p.n) : [], 'Selecciona una provincia', selected);
      this.fillDistricts();
    }

    fillDistricts(selected) {
      const province = this.currentProvince();
      this.fillSelect(this.selects.district, province ? province.d : [], 'Selecciona un distrito', selected);
      this.toggleSave();
    }

    toggleSave() {
      this.querySelector('[data-delivery-save]').disabled = !this.selects.district.value;
    }

    save() {
      const department = this.currentDepartment();
      const province = this.currentProvince();
      if (!department || !province || !this.selects.district.value) return;
      this.location = {
        department: department.n,
        province: province.n,
        district: this.selects.district.value,
        code: province.c || department.c,
      };
      saveLocation(this.location);
      this.dialog.close();
      this.render();
    }

    async render() {
      if (!this.location) return;
      try {
        const snapshot = await loadJson(this.dataset.zonesUrl);
        const zone = snapshot.zones.find((z) => z.provinces.includes(this.location.code));
        const place = `${this.location.district}, ${this.location.province}`;
        const change =
          `<p class="alu-pdp-delivery-place">Enviar a ${escapeHtml(place)}</p>` +
          `<button class="alu-pdp-link" type="button" data-delivery-open>Cambiar ubicación ${this.caret}</button>`;

        if (!zone) {
          this.result.innerHTML = `<strong>Sin despacho a esta ubicación</strong><p>Prueba con retiro en tienda.</p>${change}`;
          return;
        }

        // Tarifa para este producto solo (una unidad): no se suma el carrito ni la cantidad.
        const subtotal = this.price;
        const applicable = zone.methods.filter((m) => (m.min === null || subtotal >= m.min) && (m.max === null || subtotal <= m.max));
        const methods = applicable.length ? applicable : zone.methods;
        const cheapest = methods.reduce((min, m) => (m.price < min.price ? m : min), methods[0]);

        const eta = this.eta[zone.name.toLowerCase()];
        const names = [...new Set(methods.map((m) => m.name))];
        // Tarifa en S/ 0 con "pago en destino" (provincias): el costo lo cobra el courier
        // al recibir, así que no es envío gratis.
        const onDelivery = cheapest.price === 0 && methods.some((m) => /destino/i.test(m.description));
        let cost;
        let detail = '';
        if (onDelivery) {
          cost = 'Costo de envío: pago en destino';
          detail = `Vía ${names.join(' u ')}`;
        } else if (cheapest.price === 0) {
          cost = 'Envío gratis';
        } else {
          cost = `Envío: ${formatMoney(cheapest.price, this.moneyFormat)}`;
        }
        // Si la zona tiene varios couriers (SHALOM, OLVA…), se nombran.
        if (!detail && names.length > 1) detail = `Vía ${names.join(' u ')}`;

        this.result.innerHTML =
          `<strong>${escapeHtml(this.title)}</strong>` +
          (eta ? `<p class="alu-pdp-delivery-eta">${escapeHtml(eta)}</p>` : '') +
          `<p class="alu-pdp-delivery-cost">${escapeHtml(cost)}${detail ? ` · ${escapeHtml(detail)}` : ''}</p>` +
          change;
      } catch (error) {
        console.error('[alu-delivery] No se pudo calcular el envío', error);
        this.result.innerHTML = this.initialResult;
      }
    }
  });
})();
